// Companies page - lists the companies the current user belongs to.
// Firestore rules only allow reading a company document if the user is a
// member, so we iterate memberships rather than doing a collection query.

import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import { ROLE_LABELS, type Company } from '../types/models';
import { formatTimestamp } from '../lib/format';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/Page';
import { usePageTitle } from '../hooks/usePageTitle';

export default function CompaniesPage() {
  usePageTitle('Companies');
  const { memberships, activeCompanyId, setActiveCompanyId } = useAuth();
  const [companies, setCompanies] = useState<Record<string, Company | null>>(
    {}
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const entries: Array<[string, Company | null]> = await Promise.all(
        memberships.map(async (m) => {
          try {
            const snap = await getDoc(doc(db, 'companies', m.companyId));
            if (snap.exists()) {
              const data = snap.data() as Omit<Company, 'id'>;
              return [m.companyId, { id: snap.id, ...data }] as [
                string,
                Company
              ];
            }
            return [m.companyId, null] as [string, null];
          } catch {
            return [m.companyId, null] as [string, null];
          }
        })
      );
      if (!cancelled) {
        setCompanies(Object.fromEntries(entries));
        setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [memberships]);

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        title="Companies"
        description="Every company this login belongs to. Jobs, customers and evidence are kept completely separate between them."
      />

      <div className="mt-5 flex flex-col gap-2">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-[14px] border border-hair bg-elev px-4 py-4">
              <div className="skeleton h-4 w-40 rounded-md" />
              <div className="skeleton mt-2 h-3.5 w-28 rounded-md" />
            </div>
          ))
        ) : (
          memberships.map((m) => {
            const company = companies[m.companyId];
            const isActive = activeCompanyId === m.companyId;
            return (
              <div key={m.companyId}
                className={`flex flex-wrap items-center gap-3 rounded-[14px] border bg-elev px-4 py-3.5 ${isActive ? 'border-accent' : 'border-hair'}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {isActive && <span className="h-2 w-2 shrink-0 rounded-full bg-st-completed" aria-hidden />}
                    <span className="truncate text-[16px] font-semibold text-ink">{company?.name || m.companyId}</span>
                  </div>
                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-ink3">
                    <span className="font-mono">{m.companyId}</span>
                    <span>{ROLE_LABELS[m.role]}</span>
                    {company?.createdAt && <span>Since <span className="font-mono">{formatTimestamp(company.createdAt)}</span></span>}
                  </div>
                </div>
                {isActive ? (
                  <span className="eyebrow text-accent">Current</span>
                ) : (
                  <Button type="button" variant="secondary" size="sm" onClick={() => setActiveCompanyId(m.companyId)}>
                    Switch to this
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      <p className="ml-1 mt-4 text-[13px] text-ink3">
        Uploads already in progress continue whichever company you are looking at.
      </p>
    </div>
  );
}
